<template>
  <div class="multi-image-uploader">
    <div class="image-grid">
      <!-- 已选图片 -->
      <div v-for="(img, index) in value" :key="index" class="grid-item">
        <img :src="img" class="grid-img" alt="图片" />
        <div class="grid-mask" @click="handleRemove(index)">
          <i class="el-icon-delete" />
          <span>移除</span>
        </div>
      </div>
      <!-- 上传入口 -->
      <el-upload
        v-if="value.length < max"
        class="grid-item grid-upload"
        action="#"
        :show-file-list="false"
        :auto-upload="false"
        multiple
        accept="image/*"
        :on-change="handleChange"
      >
        <i :class="uploading ? 'el-icon-loading' : 'el-icon-plus'" />
        <span class="upload-tip">{{ uploading ? '上传中' : (value.length + '/' + max) }}</span>
      </el-upload>
    </div>
    <p v-if="hint" class="uploader-hint">{{ hint }}</p>
  </div>
</template>

<script>
import uploadToQiniu from '@/util/qiniu-upload'

export default {
  name: 'MultiImageUploader',
  props: {
    // v-model：图片 URL 数组（七牛外链）
    value: { type: Array, default: () => [] },
    // 最多可上传张数（图文帖产品规则：最多 6 张）
    max: { type: Number, default: 6 },
    hint: { type: String, default: '' },
    // 七牛存储 key 前缀
    dir: { type: String, default: 'common' },
    // 预留覆盖：传 raw => Promise<url> 则不走默认七牛直传
    upload: { type: Function, default: null }
  },
  data() {
    return {
      uploading: false,
      pending: []
    }
  },
  methods: {
    handleChange(file) {
      const raw = file.raw
      if (!raw) return
      if (!/^image\//.test(raw.type)) {
        this.$message.error('只能上传图片文件')
        return
      }
      if (raw.size / 1024 / 1024 > 5) {
        this.$message.error('单张图片不能超过 5MB')
        return
      }
      // 已占用 + 排队中的总数和上限比较，防止并发选图超张
      if (this.value.length + this.pending.length >= this.max) {
        this.$message.warning(`最多上传 ${this.max} 张图片`)
        return
      }
      this.pending.push(raw)
      this.drain()
    },
    // 逐张串行上传：先选的先传完，保证图集顺序与选择顺序一致
    drain() {
      if (this.uploading) return
      const raw = this.pending.shift()
      if (!raw) return
      this.uploading = true
      const task = this.upload ? this.upload(raw) : uploadToQiniu(raw, this.dir)
      task.then(url => {
        this.push(url)
      }).catch(() => {
        this.$message.error('有图片上传失败，请重试')
      }).finally(() => {
        this.uploading = false
        this.drain()
      })
    },
    push(url) {
      const next = this.value.concat([url]).slice(0, this.max)
      this.$emit('input', next)
      this.$emit('change', next)
    },
    handleRemove(index) {
      const next = this.value.slice()
      next.splice(index, 1)
      this.$emit('input', next)
      this.$emit('change', next)
    }
  }
}
</script>

<style scoped>
.multi-image-uploader {
  width: 100%;
}

.image-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.grid-item {
  position: relative;
  width: 96px;
  height: 96px;
  border-radius: 6px;
  overflow: hidden;
  background: #f5f6f8;
}

.grid-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.grid-mask {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  color: #fff;
  font-size: 12px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  opacity: 0;
  cursor: pointer;
  transition: opacity 0.2s;
}

.grid-item:hover .grid-mask {
  opacity: 1;
}

.grid-upload >>> .el-upload {
  width: 96px;
  height: 96px;
  border: 1px dashed #dcdfe6;
  border-radius: 6px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  color: #8c939d;
  cursor: pointer;
  transition: border-color 0.2s;
}

.grid-upload >>> .el-upload:hover {
  border-color: var(--primary);
}

.grid-upload i {
  font-size: 22px;
}

.upload-tip {
  font-size: 12px;
}

.uploader-hint {
  margin: 8px 0 0;
  font-size: 12px;
  color: #909399;
  line-height: 1.5;
}
</style>
